import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { Modal } from "react-bootstrap";
import HodSchedulerGrid from "./HodSchedulerGrid";
import HodAllocateModal from "./HodAllocateModal";
import {
  fetchAssignments,
  deleteAssignment,
  fetchCalendar,
} from "../../../services/assignmentApi";
import { fetchClients, getClientImageSrc }  from "../../../services/clientApi";
import { fetchProjects } from "../../../services/projectApi";

function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  return d;
}

function fmt(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function normalizeId(v) {
  return v && typeof v === "object" ? v._id : v;
}

const SELECT_STYLE = {
  paddingLeft: 32, paddingRight: 28, paddingTop: 7, paddingBottom: 7,
  borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12,
  color: "#334155", background: "#fff", cursor: "pointer", outline: "none",
  fontWeight: 500, appearance: "none", WebkitAppearance: "none",
};

const HodView = () => {
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  // Filters (Client, Project, Assigned By)
  const [filterClientId,     setFilterClientId]     = useState("");
  const [filterProjectId,    setFilterProjectId]    = useState("");
  const [filterAssignedById, setFilterAssignedById] = useState("");

  // Dropdown options
  const [clients,  setClients]  = useState([]);
  const [projects, setProjects] = useState([]);

  // Assignment data
  const [assignments,  setAssignments]  = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // Allocate modal
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);

  // Detail popup
  const [detailCard, setDetailCard] = useState(null);

  // Load dropdown data once
  useEffect(() => {
    fetchClients().then(setClients).catch(() => {});
    fetchProjects().then(setProjects).catch(() => {});
  }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 4);
    return d;
  }, [weekStart]);

  // Load assignments + calendar whenever the week or filters change
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = {};
    if (filterClientId)  params.clientId  = filterClientId;
    if (filterProjectId) params.projectId = filterProjectId;

    Promise.all([
      fetchAssignments(params),
      fetchCalendar(isoDate(weekStart), isoDate(weekEnd)),
    ])
      .then(([asgns, cal]) => {
        setAssignments(asgns);
        setCalendarData(cal);
      })
      .catch((err) => setError(err.message || "Failed to load assignments."))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd, filterClientId, filterProjectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handlePrev = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const handleNext = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };

  const openEditModal = (assignment) => { setEditSchedule(assignment); setModalOpen(true); };
  const closeModal    = () => { setModalOpen(false); setEditSchedule(null); };
  const openDetail    = (card) => setDetailCard(card);
  const handleSaved   = () => { closeModal(); loadData(); };

  // Projects filtered by selected client (inactive projects excluded)
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (p.isActive === false) return false;
      if (!filterClientId) return true;
      const pid = p.clientId && typeof p.clientId === "object" ? p.clientId._id : p.clientId;
      return String(pid) === String(filterClientId);
    });
  }, [projects, filterClientId]);

  // Unique "Assigned By" people derived from the loaded calendar data.
  // Narrowed by the Client/Project filters so the options stay in sync with them.
  const assigners = useMemo(() => {
    const map = new Map();
    calendarData.forEach((emp) => {
      (emp.assignments || []).forEach((a) => {
        const clientId  = String(normalizeId(a.clientId)  || a.clientName  || "");
        const projectId = String(normalizeId(a.projectId) || a.projectName || "");
        if (filterClientId  && clientId  !== filterClientId)  return;
        if (filterProjectId && projectId !== filterProjectId) return;
        const id = a.assignedById ? String(a.assignedById) : "";
        if (!id) return;
        if (!map.has(id)) map.set(id, a.assignedBy || "Unknown");
      });
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [calendarData, filterClientId, filterProjectId]);

  // Clear the Assigned By selection when it is no longer valid for the current data/filters.
  useEffect(() => {
    if (filterAssignedById && !assigners.some((a) => a.id === filterAssignedById)) {
      setFilterAssignedById("");
    }
  }, [assigners, filterAssignedById]);

  // Map client _id and clientName → uploaded image URL for the grid's first column.
  // Falls back to initials in the grid when a client has no image.
  const clientImages = useMemo(() => {
    const map = {};
    for (const client of clients) {
      if (!client.clientImage) continue;
      const src = getClientImageSrc(client.clientImage);
      if (!src) continue;
      if (client._id)        map[String(client._id)] = src;
      if (client.clientName) map[client.clientName]   = src;
    }
    return map;
  }, [clients]);

  return (
    <Fragment>
      <div>
        {/* Controls bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, background: "#fff", borderRadius: 12, padding: "14px 20px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>

          {/* Row 1: date range + ADD JOB */}
          <div className="d-flex align-items-center justify-content-between mb-3 col-12" style={{ gap: 12, flexWrap: "wrap" }}>
            <div className="d-flex align-items-center gap-2">
              <button onClick={handlePrev}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 11 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <i className="fa fa-chevron-left" />
              </button>
              <i className="fa fa-calendar" style={{ color: "#94a3b8", fontSize: 13 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }}>
                {fmt(weekStart)} – {fmt(weekEnd)}
              </span>
              <button onClick={handleNext}
                style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 11 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <i className="fa fa-chevron-right" />
              </button>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setEditSchedule(null); setModalOpen(true); }}
              style={{ borderRadius: 4, fontWeight: 700, whiteSpace: "nowrap", padding: "8px 18px" }}
            >
              <i className="fa fa-plus me-1" style={{ fontSize: 11 }} /> ADD JOB
            </button>
          </div>

          {/* Row 2: Client + Project filters only */}
          <div className="d-flex align-items-center justify-content-end gap-2 col-12 flex-wrap" style={{ overflowX: "auto" }}>
            {/* Client filter */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <i className="fa fa-building" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
              <select
                value={filterClientId}
                onChange={(e) => { setFilterClientId(e.target.value); setFilterProjectId(""); }}
                style={SELECT_STYLE}
              >
                <option value="">Select Client</option>
                {clients.map((c) => <option key={c._id} value={c._id}>{c.clientName}</option>)}
              </select>
              <i className="fa fa-angle-down" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
            </div>

            {/* Project filter */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <i className="fa fa-folder" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                style={SELECT_STYLE}
              >
                <option value="">Select Project</option>
                {filteredProjects.map((p) => <option key={p._id} value={p._id}>{p.projectName}</option>)}
              </select>
              <i className="fa fa-angle-down" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
            </div>

            {/* Assigned By filter */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <i className="fa fa-user-plus" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
              <select
                value={filterAssignedById}
                onChange={(e) => setFilterAssignedById(e.target.value)}
                style={SELECT_STYLE}
              >
                <option value="">Assigned By</option>
                {assigners.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <i className="fa fa-angle-down" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 11, pointerEvents: "none" }} />
            </div>

            {/* Clear filters */}
            {(filterClientId || filterProjectId || filterAssignedById) && (
              <button
                onClick={() => { setFilterClientId(""); setFilterProjectId(""); setFilterAssignedById(""); }}
                style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, cursor: "pointer", fontWeight: 500 }}
              >
                <i className="fa fa-times me-1" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between">
            <span><i className="fa fa-exclamation-circle me-2" />{error}</span>
            <button className="btn btn-sm btn-outline-danger" onClick={loadData}>
              <i className="fa fa-refresh me-1" /> Retry
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="mt-2 text-muted">Loading assignments…</p>
          </div>
        ) : (
          <div style={{ marginBottom: 28 }}>
            <HodSchedulerGrid
              calendarData={calendarData}
              weekStart={weekStart}
              filterClientId={filterClientId}
              filterProjectId={filterProjectId}
              filterAssignedById={filterAssignedById}
              projects={projects}
              clientImages={clientImages}
              onCardClick={openDetail}
            />
          </div>
        )}

        {/* Allocate Modal */}
        <HodAllocateModal
          isOpen={modalOpen}
          onClose={closeModal}
          editData={editSchedule}
          onSaved={handleSaved}
        />

        {/* Project Detail Modal */}
        <Modal show={!!detailCard} onHide={() => setDetailCard(null)} centered>
          <Modal.Header closeButton className="border-bottom pb-3">
            <Modal.Title style={{ fontSize: 16 }}>
              <i className="fa fa-folder-open me-2 text-primary" />
              Project Details
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {detailCard && (() => {
              const { assignment, color } = detailCard;
              const pairs = Array.isArray(assignment?.employeeTaskPairs) ? assignment.employeeTaskPairs : [];

              return (
                <div style={{
                  borderLeft:   `4px solid ${color.border}`,
                  background:   color.bg,
                  borderRadius: "0 10px 10px 0",
                  padding:      18,
                }}>
                  {/* Client Name */}
                  <div style={{ fontSize: 15, fontWeight: 700, color: color.border, marginBottom: 14 }}>
                    <i className="fa fa-building me-2" />
                    {assignment?.clientName || "—"}
                  </div>

                  {/* Project Name */}
                  <div className="mb-2" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <strong style={{ minWidth: 100, flexShrink: 0, color: "#334155" }}>Project Name:</strong>
                    <span style={{ color: "#495057" }}>{assignment?.projectName || "—"}</span>
                  </div>

                  {/* Start Date */}
                  <div className="mb-2" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <strong style={{ minWidth: 100, flexShrink: 0, color: "#334155" }}>Start Date:</strong>
                    <span style={{ color: "#495057" }}>{fmtDate(assignment?.projectStartDate)}</span>
                  </div>

                  {/* End Date */}
                  <div className="mb-3" style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <strong style={{ minWidth: 100, flexShrink: 0, color: "#334155" }}>End Date:</strong>
                    <span style={{ color: "#495057" }}>{fmtDate(assignment?.projectEndDate)}</span>
                  </div>

                  {/* Employee / SOW table */}
                  {pairs.length > 0 && (
                    <div style={{ borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: 12 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{
                              padding: "7px 10px",
                              background: color.tagBg,
                              borderBottom: `2px solid ${color.border}`,
                              color: "#334155",
                              fontWeight: 700,
                              textAlign: "left",
                              width: "45%",
                            }}>
                              Employee
                            </th>
                            <th style={{
                              padding: "7px 10px",
                              background: color.tagBg,
                              borderBottom: `2px solid ${color.border}`,
                              color: "#334155",
                              fontWeight: 700,
                              textAlign: "left",
                            }}>
                              Task
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pairs.map((pair, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                              <td style={{ padding: "7px 10px", color: "#334155", fontWeight: 500 }}>
                                {pair.employee}
                              </td>
                              <td style={{ padding: "7px 10px", color: "#495057" }}>
                                {pair.notes}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </Modal.Body>

          <Modal.Footer>
            <button className="btn btn-secondary btn-sm" onClick={() => setDetailCard(null)}>
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </Fragment>
  );
};

export default HodView;
